import { useEffect, useState } from "react"

import {
  acceptDealAskRobSuggestion,
  getDealAskRob,
  rejectDealAskRobSuggestion,
  sendDealAskRobMessage,
} from "@/lib/api"
import { useSession } from "@/lib/session-context"

function looksPersistedDealId(id) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id)
}

export function useDealAskRob({ deal, tab = "overview", onSuggestionAccepted } = {}) {
  const { currentUser, csrfToken } = useSession()
  const canUsePersistedDealApi = Boolean(currentUser && deal?.id && looksPersistedDealId(deal.id))
  const [thread, setThread] = useState(null)
  const [input, setInput] = useState("")
  const [isLoading, setIsLoading] = useState(canUsePersistedDealApi)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [busySuggestionId, setBusySuggestionId] = useState(null)
  const [error, setError] = useState(null)
  const messages = thread?.messages ?? []
  const pendingSuggestions = (thread?.suggestions ?? []).filter((suggestion) => suggestion.status === "pending")

  useEffect(() => {
    if (!canUsePersistedDealApi) return undefined

    let ignore = false

    getDealAskRob(deal.id, { csrfToken })
      .then((payload) => {
        if (ignore) return

        setThread(payload.thread ?? null)
        setError(null)
      })
      .catch((requestError) => {
        if (!ignore) setError(requestError.message)
      })
      .finally(() => {
        if (!ignore) setIsLoading(false)
      })

    return () => { ignore = true }
  }, [csrfToken, deal?.id, canUsePersistedDealApi])

  async function submitMessage(message) {
    const body = message.trim()
    if (!body || isSubmitting || !canUsePersistedDealApi) return

    setIsSubmitting(true)
    setError(null)

    try {
      const payload = await sendDealAskRobMessage(deal.id, {
        body,
        metadata: { tab },
      }, { csrfToken })
      setThread(payload.thread ?? null)
      setInput("")
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  async function acceptSuggestion(suggestionId) {
    if (!canUsePersistedDealApi || busySuggestionId) return

    setBusySuggestionId(suggestionId)
    setError(null)

    try {
      const payload = await acceptDealAskRobSuggestion(deal.id, suggestionId, { csrfToken })
      setThread(payload.thread ?? null)
      onSuggestionAccepted?.(payload)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setBusySuggestionId(null)
    }
  }

  async function rejectSuggestion(suggestionId) {
    if (!canUsePersistedDealApi || busySuggestionId) return

    setBusySuggestionId(suggestionId)
    setError(null)

    try {
      const payload = await rejectDealAskRobSuggestion(
        deal.id,
        suggestionId,
        { reason: "Dismissed from deal card" },
        { csrfToken },
      )
      setThread(payload.thread ?? null)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setBusySuggestionId(null)
    }
  }

  return {
    input,
    setInput,
    messages,
    pendingSuggestions,
    isLoading,
    isSubmitting,
    busySuggestionId,
    error,
    canUsePersistedDealApi,
    submitMessage,
    acceptSuggestion,
    rejectSuggestion,
  }
}
